import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bookmark, Trash2 } from "lucide-react";
import {
  useLateralSavedFilters,
  NONE,
  LateralFilterContext,
} from "@/hooks/useLateralSavedFilters";

interface Props {
  context: LateralFilterContext;
  /** Current filter values to snapshot when saving */
  currentFilters: Record<string, any>;
  /** Called when user picks a saved filter — apply these values */
  onApply: (filters: Record<string, any>) => void;
  /** Called when user picks the "None" sentinel — clear filters */
  onClear: () => void;
}

export function LateralSavedFiltersBar({ context, currentFilters, onApply, onClear }: Props) {
  const {
    savedFilters,
    selected,
    setSelected,
    saveCurrentFilters,
    deleteSelected,
    isSaving,
    isDeleting,
  } = useLateralSavedFilters(context);

  const handleSelect = (name: string) => {
    if (name === NONE) {
      setSelected(NONE);
      onClear();
      return;
    }
    const found = savedFilters.find((f) => f.name === name);
    if (!found) return;
    setSelected(name);
    onApply(found.filters);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-gray-500 shrink-0">Saved filters:</span>

      <Select value={selected} onValueChange={handleSelect}>
        <SelectTrigger className="h-7 w-44 text-xs">
          <SelectValue placeholder="Choose a filter…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— None —</SelectItem>
          {savedFilters.map((f) => (
            <SelectItem key={f.id} value={f.name}>
              {f.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs gap-1"
        onClick={() => saveCurrentFilters(currentFilters)}
        disabled={isSaving}
      >
        <Bookmark className="h-3 w-3" />
        {isSaving ? "Saving…" : "Save current"}
      </Button>

      {selected !== NONE && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs gap-1 text-red-500 hover:text-red-700"
          onClick={deleteSelected}
          disabled={isDeleting}
        >
          <Trash2 className="h-3 w-3" />
          {isDeleting ? "Deleting…" : "Delete"}
        </Button>
      )}
    </div>
  );
}
